/** @jsx jsx */
import { jsx, css } from "@emotion/react";
import { useState } from "react";
import PropTypes from "prop-types";
import { useMeasure } from "react-use";
import * as commonStyles from "../explorer/styles";

const containerCSS = ({ containerWidth, withImage }) => css`
    overflow: hidden;
    padding-left: 16px;
    padding-right: 16px;
    padding-bottom: 30px;
    padding-top: 48px;
    position: relative;
    box-sizing: border-box;

    &::after {
        background-color: #504cf3;
        content: "";
        height: 100%;
        left: 0;
        position: absolute;
        top: 0;
        transform-origin: top left;
        width: 100%;
        pointer-events: none;
    }

    ${containerWidth >= 500 &&
        `
        padding-left: 24px;
        padding-right: 24px;
    `}

    ${containerWidth >= 700 &&
        `
        padding-left: 36px;
        padding-right: 36px;
        padding-bottom: 36px;
    `}

    ${withImage &&
        `
        padding-top: 0;

        &::after {
            top: ${184 / 2}px;
            transform: skewY(2.5deg);
        }

        ${containerWidth >= 500 &&
            `
            &::after {
                top: ${240 / 2}px;
            }
        `}
    `}
`;
const imgContainerCSS = ({ containerWidth, imgHasLoaded }) => css`
    margin: 0 0 24px;
    position: relative;
    z-index: 1;

    img {
        ${commonStyles.hideImgAlt}

        border-radius: 8px;
        box-shadow: 0px 4px 12px rgba(42, 37, 199, 0.5);
        display: block;
        width: 100%;
        background-color: #a7a5f9;
        opacity: 0;
        transition: opacity 150ms ease-out;
    }

    ${imgHasLoaded &&
        `
        img {
            opacity: 1;
        }
    `}

    ${containerWidth >= 700 &&
        `
        margin: 0 0 36px;
    `}
`;
const titleCSS = ({ containerWidth }) => css`
    color: #ffffff;
    font-family: "Lexend Deca", sans-serif;
    font-size: 18px;
    font-weight: 400;
    line-height: 26px;
    position: relative;
    text-align: center;
    text-transform: none;
    z-index: 1;
    padding-bottom: 16px;
    margin-bottom: 16px;
    border-bottom: 1px solid #a7a5f9;
    margin-top: 0;

    ${containerWidth >= 500 &&
        `
        font-size: 24px;
        line-height: 36px;
        text-align: left;
        padding-bottom: 24px;
        margin-bottom: 24px;
    `};
`;
const descriptionCSS = ({ containerWidth }) => css`
    color: #ffffff;
    font-size: 13px;
    font-weight: 400;
    line-height: 20px;
    position: relative;
    text-align: center;
    z-index: 1;

    p {
        margin: 0;
        font-size: inherit;
    }

    a:link {
        color: #ffffff;
    }

    ${containerWidth &&
        `
        font-size: 15px;
        line-height: 24px;
        text-align: left;
        font-weight: 300;
    `}
`;

function AtlasMetadata({ className, description, name, heroImageUrl }) {
    const [imgHasLoaded, setImgHasLoaded] = useState(false);
    const [imgHasError, setImgHasError] = useState(false);
    const [containerRef, { width: containerWidth }] = useMeasure();
    const showImage = !imgHasError && heroImageUrl;
    const withImage = showImage && imgHasLoaded;

    return (
        <div ref={containerRef}>
            <header css={containerCSS({ containerWidth, withImage })} className={className}>
                {showImage && (
                    <figure css={imgContainerCSS({ containerWidth, imgHasLoaded })}>
                        <img
                            src={heroImageUrl}
                            alt={name}
                            onError={() => setImgHasError(true)}
                            onLoad={() => setImgHasLoaded(true)}
                        />
                    </figure>
                )}
                <h2 css={titleCSS({ containerWidth })}>{name}</h2>
                <div
                    css={descriptionCSS({ containerWidth })}
                    dangerouslySetInnerHTML={{
                        __html: description,
                    }}
                />
            </header>
        </div>
    );
}

AtlasMetadata.defaultProps = {
    description: "",
    name: "",
};

AtlasMetadata.propTypes = {
    description: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
};

export default AtlasMetadata;
